import { useState, useRef, useEffect } from 'react'
import { fetchMoreWebhookEvents, setContainerFilter } from '../store/statusSlice'
import type { WebhookEvent } from '../store/statusSlice'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import './WebhookEventFeed.css'

interface WebhookEventFeedProps {
  events: WebhookEvent[]
}

function getPayloadEntries(payload: Record<string, unknown>): { key: string; value: string }[] {
  const keys = Object.keys(payload)
  if (keys.length === 0) return []
  return keys.map((key) => {
    const val = payload[key]
    const str = typeof val === 'string' ? val : JSON.stringify(val)
    return { key, value: str ?? '' }
  })
}

interface ContainerDropdownProps {
  value: string
  options: string[]
  onChange: (value: string) => void
}

function ContainerDropdown({ value, options, onChange }: ContainerDropdownProps) {
  const [open, setOpen] = useState(false)
  const [focusIndex, setFocusIndex] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const allOptions = ['', ...options]

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open])

  useEffect(() => {
    if (open && menuRef.current) {
      const buttons = menuRef.current.querySelectorAll<HTMLButtonElement>('[role="option"]')
      if (focusIndex >= 0 && focusIndex < buttons.length) {
        buttons[focusIndex].focus()
      }
    }
  }, [open, focusIndex])

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      setOpen(true)
      setFocusIndex(0)
    }
  }

  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusIndex((prev) => Math.min(prev + 1, allOptions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusIndex((prev) => Math.max(prev - 1, 0))
    }
  }

  const label = value || 'All containers'

  return (
    <div className="webhook-feed__dropdown" ref={ref} data-testid="webhook-feed-filter-select" data-value={value}>
      <button
        type="button"
        ref={triggerRef}
        className="webhook-feed__dropdown-trigger"
        data-testid="webhook-feed-filter-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => { setOpen(!open); setFocusIndex(-1) }}
        onKeyDown={handleTriggerKeyDown}
      >
        {label}
        <span className="webhook-feed__dropdown-arrow" aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="webhook-feed__dropdown-menu" data-testid="webhook-feed-filter-menu" role="listbox" aria-label="Container filter" ref={menuRef} onKeyDown={handleMenuKeyDown}>
          <button
            type="button"
            role="option"
            aria-selected={value === ''}
            className={`webhook-feed__dropdown-option${value === '' ? ' webhook-feed__dropdown-option--selected' : ''}`}
            data-testid="webhook-feed-filter-option-all"
            onClick={() => { onChange(''); setOpen(false); triggerRef.current?.focus() }}
          >
            All containers
          </button>
          {options.map((id) => (
            <button
              key={id}
              type="button"
              role="option"
              aria-selected={value === id}
              className={`webhook-feed__dropdown-option${value === id ? ' webhook-feed__dropdown-option--selected' : ''}`}
              data-testid={`webhook-feed-filter-option-${id}`}
              onClick={() => { onChange(id); setOpen(false); triggerRef.current?.focus() }}
            >
              {id}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function matchesTextFilter(event: WebhookEvent, query: string): boolean {
  const q = query.toLowerCase()
  if (event.event_type.toLowerCase().includes(q)) return true
  if (event.container_id.toLowerCase().includes(q)) return true
  for (const entry of getPayloadEntries(event.payload)) {
    if (entry.key.toLowerCase().includes(q) || entry.value.toLowerCase().includes(q)) return true
  }
  return false
}

function WebhookEventFeed({ events }: WebhookEventFeedProps) {
  const dispatch = useAppDispatch()
  const containerFilter = useAppSelector((state) => state.status.containerFilter)
  const containerIds = useAppSelector((state) => state.status.containerIds)
  const [loadingMore, setLoadingMore] = useState(false)
  const [exhausted, setExhausted] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [textFilter, setTextFilter] = useState('')

  const handleShowMore = async () => {
    if (events.length === 0) return
    const oldest = events[events.length - 1].received_at
    setLoadingMore(true)
    const result = await dispatch(fetchMoreWebhookEvents({ before: oldest, containerId: containerFilter }))
    setLoadingMore(false)
    if (fetchMoreWebhookEvents.fulfilled.match(result) && result.payload.length === 0) {
      setExhausted(true)
    }
  }

  const handleFilterChange = (value: string) => {
    setExhausted(false)
    dispatch(setContainerFilter(value || null))
  }

  return (
    <section className="webhook-feed" data-testid="webhook-event-feed" aria-labelledby="webhook-feed-heading">
      <h2 className="sr-only" id="webhook-feed-heading">Recent Webhook Events</h2>
      <button
        type="button"
        className="webhook-feed__title"
        data-testid="webhook-feed-toggle"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed(!collapsed)}
      >
        <span>Recent Webhook Events</span>
        <span className="webhook-feed__toggle-icon" aria-hidden="true">{collapsed ? '+' : '\u2013'}</span>
      </button>
      {!collapsed && (
        <div className="webhook-feed__filter-bar" data-testid="webhook-feed-filter">
          <input
            type="text"
            className="webhook-feed__text-filter"
            data-testid="webhook-feed-text-filter"
            placeholder="Filter events..."
            aria-label="Filter webhook events"
            value={textFilter}
            onChange={(e) => setTextFilter(e.target.value)}
          />
          {containerIds.length > 0 && (
            <>
              <label className="webhook-feed__filter-label" htmlFor="container-filter">
                Container:
              </label>
              <ContainerDropdown
                value={containerFilter ?? ''}
                options={containerIds}
                onChange={handleFilterChange}
              />
            </>
          )}
          {(containerFilter || textFilter) && (
            <button
              type="button"
              className="webhook-feed__filter-clear"
              data-testid="webhook-feed-filter-clear"
              onClick={() => { handleFilterChange(''); setTextFilter('') }}
            >
              Clear filter
            </button>
          )}
        </div>
      )}
      {!collapsed && (() => {
        const filteredEvents = textFilter ? events.filter((e) => matchesTextFilter(e, textFilter)) : events
        return filteredEvents.length === 0 ? (
        <p className="webhook-feed__empty" data-testid="webhook-feed-empty">
          {containerFilter || textFilter ? 'No webhook events match the filter' : 'No webhook events received'}
        </p>
      ) : (
        <>
          <ul className="webhook-feed__list" role="list">
            {filteredEvents.map((event) => (
              <li key={event.id} className="webhook-feed__item" data-testid="webhook-event-item">
                <div className="webhook-feed__item-header">
                  <span className="webhook-feed__timestamp" data-testid="webhook-event-timestamp">
                    {new Date(event.received_at).toLocaleString()}
                  </span>
                  <span
                    className="webhook-feed__container-id"
                    data-testid="webhook-event-container-id"
                  >
                    {event.container_id}
                  </span>
                  <span className="webhook-feed__event-type" data-testid="webhook-event-type">
                    {event.event_type}
                  </span>
                </div>
                <div className="webhook-feed__payload" data-testid="webhook-event-payload">
                  {getPayloadEntries(event.payload).length === 0 ? (
                    '—'
                  ) : (
                    getPayloadEntries(event.payload).map((entry) => (
                      <div key={entry.key} className="webhook-feed__payload-entry">
                        <span className="webhook-feed__payload-key">{entry.key}:</span>{' '}
                        <span className="webhook-feed__payload-value">{entry.value}</span>
                      </div>
                    ))
                  )}
                </div>
              </li>
            ))}
          </ul>
          {!exhausted && (
            <button
              type="button"
              className="webhook-feed__show-more"
              data-testid="webhook-feed-show-more"
              onClick={handleShowMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : 'Show more'}
            </button>
          )}
        </>
      )
      })()}
    </section>
  )
}

export default WebhookEventFeed
